import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { toast } from "sonner";

function formatSlot(iso, lang) {
  const d = new Date(iso);
  return d.toLocaleString(lang === "fr" ? "fr-FR" : "en-US", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function Booking() {
  const { t, lang } = useI18n();
  const [slots, setSlots] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", party_size: 2, travel_type: "discovery", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    api.get("/bookings/slots").then((r) => setSlots(r.data.slots)).catch(() => setSlots([]));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) { toast.error(t("booking.pick_slot")); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/bookings", {
        ...form,
        party_size: Number(form.party_size),
        slot_iso: selected,
        language: lang,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      setConfirmed(r.data);
      toast.success(t("booking.success"));
    } catch {
      toast.error(lang === "fr" ? "Créneau indisponible." : "Slot unavailable.");
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-32 text-center" data-testid="booking-confirmed">
        <div className="overline text-[#D4AF37]">{lang === "fr" ? "Rendez-vous confirmé" : "Booking confirmed"}</div>
        <h1 className="font-serif font-light text-5xl md:text-6xl text-[#0A1128] mt-6">{t("booking.success")}</h1>
        <p className="mt-8 text-[#0A1128]/70">
          {lang === "fr" ? "Lien visio : " : "Video link: "}
          <a href={confirmed.meet_link} className="text-[#D4AF37] underline">{confirmed.meet_link}</a>
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16 pb-32 grid lg:grid-cols-12 gap-16">
      <div className="lg:col-span-5">
        <div className="overline text-[#D4AF37]">{lang === "fr" ? "Rendez-vous" : "Booking"}</div>
        <h1 className="font-serif font-light text-5xl md:text-6xl text-[#0A1128] mt-4 leading-tight">{t("booking.title")}</h1>
        <p className="mt-6 text-[#0A1128]/70 max-w-md">{t("booking.lede")}</p>
        <div className="mt-10 p-6 bg-white border border-[#D4AF37]/30">
          <div className="overline text-[#D4AF37]">{lang === "fr" ? "Ce qui vous attend" : "What to expect"}</div>
          <ul className="mt-4 space-y-3 text-sm text-[#0A1128]/80">
            <li>• 30 min — {lang === "fr" ? "Visio avec Lucile" : "Video with Lucile"}</li>
            <li>• {lang === "fr" ? "FR ou EN, sans engagement" : "FR or EN, no obligation"}</li>
            <li>• {lang === "fr" ? "Suivi par email sous 24h" : "Follow-up by email within 24h"}</li>
          </ul>
        </div>
      </div>

      <form onSubmit={submit} className="lg:col-span-7 space-y-8" data-testid="booking-form">
        <div>
          <div className="overline text-[#0A1128]/60 mb-3">{t("booking.pick_slot")}</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-[280px] overflow-y-auto pr-2" data-testid="booking-slots">
            {slots.map((s) => (
              <button
                type="button"
                key={s}
                onClick={() => setSelected(s)}
                className={`text-left border px-4 py-3 text-sm transition-colors ${
                  selected === s
                    ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#0A1128]"
                    : "border-[#0A1128]/15 text-[#0A1128]/80 hover:border-[#0A1128]"
                }`}
                data-testid={`slot-${s}`}
              >
                {formatSlot(s, lang)}
              </button>
            ))}
            {slots.length === 0 && (
              <div className="col-span-full text-sm text-[#0A1128]/50">{lang === "fr" ? "Chargement…" : "Loading…"}</div>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="overline text-[#0A1128]/60">{t("booking.form_name")}</label>
            <input required value={form.name} onChange={set("name")} className="w-full bg-transparent border-b border-[#0A1128]/30 py-3 focus:outline-none focus:border-[#D4AF37]" data-testid="booking-name" />
          </div>
          <div>
            <label className="overline text-[#0A1128]/60">Email</label>
            <input required type="email" value={form.email} onChange={set("email")} className="w-full bg-transparent border-b border-[#0A1128]/30 py-3 focus:outline-none focus:border-[#D4AF37]" data-testid="booking-email" />
          </div>
          <div>
            <label className="overline text-[#0A1128]/60">{t("booking.form_phone")}</label>
            <input value={form.phone} onChange={set("phone")} className="w-full bg-transparent border-b border-[#0A1128]/30 py-3 focus:outline-none focus:border-[#D4AF37]" data-testid="booking-phone" />
          </div>
          <div>
            <label className="overline text-[#0A1128]/60">{t("booking.form_party")}</label>
            <input type="number" min={1} value={form.party_size} onChange={set("party_size")} className="w-full bg-transparent border-b border-[#0A1128]/30 py-3 focus:outline-none focus:border-[#D4AF37]" data-testid="booking-party" />
          </div>
        </div>

        <div>
          <label className="overline text-[#0A1128]/60">{t("booking.form_type")}</label>
          <select value={form.travel_type} onChange={set("travel_type")} className="w-full bg-transparent border-b border-[#0A1128]/30 py-3 focus:outline-none focus:border-[#D4AF37]" data-testid="booking-type">
            {["discovery", "wedding", "champagne", "private", "honeymoon", "proposal", "corporate"].map((k) => (
              <option key={k} value={k}>{t(`booking.types.${k}`)}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="overline text-[#0A1128]/60">{t("booking.form_notes")}</label>
          <textarea rows={4} value={form.notes} onChange={set("notes")} className="w-full bg-transparent border-b border-[#0A1128]/30 py-3 focus:outline-none focus:border-[#D4AF37] resize-none" data-testid="booking-notes" />
        </div>

        <button type="submit" disabled={submitting} className="bg-[#0A1128] text-[#F9F8F6] px-10 py-4 text-xs tracking-[0.24em] uppercase hover:bg-[#D4AF37] hover:text-[#0A1128] transition-colors duration-500 disabled:opacity-60" data-testid="booking-submit">
          {submitting ? "..." : t("booking.submit")}
        </button>
      </form>
    </div>
  );
}
